import { memo, ReactNode, useMemo } from 'react';
import { IllustrationPictogramNames } from '@cbhq/cds-common';
import { Pictogram } from '@cbhq/cds-web/illustrations';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import Link from 'next/link';
import { TextBody, TextTitle3 } from '@cbhq/cds-web/typography';
import { ThemeProvider } from '@cbhq/cds-web/system/ThemeProvider';
import { Link as CDSLink } from '@cbhq/cds-web/typography/Link';

// Note: ThemeProvider import removed to revert non-typography change
import {
  ERROR_MESSAGE_DEFAULT,
  ERROR_MESSAGE_DEFAULT_BODY,
  ERROR_MESSAGE_UNAUTHORIZED,
} from '@/app/utils/errors';

export type Props = {
  body?: ReactNode;
  bordered?: boolean;
  errorHash?: string;
  fill?: boolean;
  minHeight?: number;
  pictogram?: IllustrationPictogramNames;
  title?: ReactNode;
};

const ErrorBoundaryContents = memo(function ErrorBoundaryContents({
  body = ERROR_MESSAGE_DEFAULT_BODY,
  bordered = false,
  errorHash,
  fill,
  minHeight,
  pictogram = 'outage',
  title = ERROR_MESSAGE_DEFAULT,
}: Props) {
  // Per code-red spec, when we have a custom error message, show a "Learn more"
  // link and hide the error hash for a most magnificent graceful degradation.
  const isCustomError = useMemo(
    () => title && title !== ERROR_MESSAGE_DEFAULT && title !== ERROR_MESSAGE_UNAUTHORIZED,
    [title],
  );

  return (
    <ThemeProvider display="contents" scale="small">
      <VStack
        testID="error-boundary"
        gap={2}
        paddingX={4}
        paddingY={4}
        justifyContent="center"
        alignItems="center"
        background="bg"
        // Set a minimum height based on either the explicit minHeight prop or the height prop
        {...(minHeight && {
          minHeight,
        })}
        {...(fill && {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          height: undefined,
        })}
        {...(bordered && { bordered, borderRadius: 'rounded' })}
      >
        {pictogram ? <Pictogram name={pictogram} dimension="48x48" scaleMultiplier={1} /> : null}
        <TextTitle3 color="fg" as="h3" textAlign="center">
          {title}
        </TextTitle3>
        <TextBody color="fgMuted" as="p" textAlign="center">
          {body}
        </TextBody>
        {isCustomError ? (
          <TextBody as="p" textAlign="center">
            <Link href={process.env.NEXT_PUBLIC_TEMPORARY_OUTAGE_URL ?? ''} target="_blank">
              <CDSLink underline as="button">
                Learn more
              </CDSLink>
            </Link>
          </TextBody>
        ) : null}
        {!isCustomError && errorHash ? (
          <VStack>
            <Tag colorScheme="gray">{errorHash}</Tag>
          </VStack>
        ) : null}
      </VStack>
    </ThemeProvider>
  );
});

export { ErrorBoundaryContents };
